<template>
  <ak-modal-body class="overflow-visible">
    <ak-modal-title>URL Builder</ak-modal-title>

    <ak-tabs
      v-model="activeTab"
      visible-overflow
      in-panel
      border-bottom
      @change="tabChanged"
    >
      <ak-tab-item label="Create">
        <ak-field label="Webpage URL" class="mt">
          Set a base website address (URL)
          <ak-select
            name="apps"
            :model-value="selectedApp"
            :options="apps"
            track-by="_id"
            label="title"
            @select="selectApp"
          />
        </ak-field>

        <ak-box background="grey-lightest" narrow>
          <ak-field addons>
            <ak-field-addon @click="changeProtocol">
              <strong v-if="selectedApp.isCustom" style="cursor: pointer">{{
                protocol
              }}</strong>
              <div v-else>{{ protocol }}</div>
            </ak-field-addon>
            <ak-input v-model="data.path" :readonly="!selectedApp.isCustom" />
          </ak-field>
        </ak-box>

        <hr class="form-hr" />

        <template
          v-if="
            !selectedApp.isCustom || (selectedApp.isCustom && data.path !== '')
          "
        >
          <ak-field label="URL Parameters (Optional)">
            <ak-box v-if="parameters.length" background="grey-lightest" narrow>
              <template v-for="(param, index) in parameters" :key="index">
                <ak-apps-url-builder-row
                  :param="param"
                  :update-param="updateParam"
                  :remove-param="removeParam"
                  :index="index"
                />
              </template>
            </ak-box>

            <ak-button
              ghost
              color="primary"
              class="mt-small"
              @click.prevent.stop="addParam"
              >Add Parameter</ak-button
            >
          </ak-field>

          <hr class="form-hr" />

          <ak-field label="Generated URLs" addons>
            <ak-input v-model="constructedUrl" readonly />
            <ak-button @click.stop.prevent="generateShortURL"
              >Generate Short URL</ak-button
            >
          </ak-field>

          <ak-field v-if="generatedUrl" label="Short URLs">
            <ak-input v-model="generatedUrl" readonly />
          </ak-field>
        </template>
      </ak-tab-item>

      <ak-tab-item label="History">
        <ak-field label="Short URLs" class="mt">
          Short URLs previously generated on this account
          <ak-select
            name="filter"
            :model-value="selectedFilter"
            :options="urlFilterOptions"
            track-by="_id"
            label="title"
            @select="selectFilter"
          />
        </ak-field>

        <ak-table
          v-model:data="urls"
          :per-page="historyPerPage"
          :total="historyTotal"
          :loading="historyLoading"
          :sticky-pagination="false"
          hoverable
          show-rows
          paginated
          backend-pagination
          class="mb-small b"
          @page="fetchUrls"
        >
          <template #default="{ row }">
            <ak-table-column field="createdBy" label="Created By" sortable>
              {{ row.createdByName }}
            </ak-table-column>

            <ak-table-column field="app.title" label="Page" sortable>
              {{ row.app.title }}
            </ak-table-column>

            <ak-table-column field="short" label="URL">
              <div>
                <a :href="row.short" target="_blank">{{ row.short }}</a>
              </div>
              <div>
                <a :href="row.full" target="_blank">{{ row.full }}</a>
              </div>
            </ak-table-column>
          </template>
        </ak-table>
      </ak-tab-item>
    </ak-tabs>
  </ak-modal-body>

  <ak-modal-footer>
    <ak-modal-button @click="close">Done</ak-modal-button>
  </ak-modal-footer>
</template>

<script>
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalButton from '@akerolabs/akero-vue/src/components/modal/ModalButton.vue'
import ModalFooter from '@akerolabs/akero-vue/src/components/modal/ModalFooter.vue'
import ModalTitle from '@akerolabs/akero-vue/src/components/modal/ModalTitle.vue'
import Tabs from '@akerolabs/akero-vue/src/components/tabs/Tabs.vue'
import TabItem from '@akerolabs/akero-vue/src/components/tabs/TabItem.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import FieldAddon from '@akerolabs/akero-vue/src/components/field/FieldAddon.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Table from '@akerolabs/akero-vue/src/components/table/Table.vue'
import TableColumn from '@akerolabs/akero-vue/src/components/table/TableColumn.vue'
import { agencyObj, accountObj } from '@akerolabs/akero-vue/src/store'
import { makePublishDomain } from '@akerolabs/akero-vue/src/services/apps'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import Row from './BuilderRow.vue'
import { sendEvent } from '../../../services/intercom.io'

import {
  merge,
  uuid,
  createQueryString,
} from '@akerolabs/akero-vue/src/utils/helpers'

import { defineComponent, ref, computed, inject, onMounted } from 'vue'

/**
 * @type {Object}
 * @description This is the custom url option for the select menu
 */
const customOption = {
  _id: uuid(),
  title: 'Custom URL',
  isCustom: true,
}

/**
 * @type {Object}
 * @description An all option for the history filter
 */
const allOption = {
  _id: uuid(),
  title: 'All',
  showAll: true,
}

export default defineComponent({
  name: 'AkAppsUrlBuilder',

  components: {
    [ModalBody.name]: ModalBody,
    [ModalButton.name]: ModalButton,
    [ModalFooter.name]: ModalFooter,
    [ModalTitle.name]: ModalTitle,
    [Tabs.name]: Tabs,
    [TabItem.name]: TabItem,
    [Select.name]: Select,
    [Field.name]: Field,
    [FieldAddon.name]: FieldAddon,
    [Input.name]: Input,
    [Box.name]: Box,
    [Icon.name]: Icon,
    [Row.name]: Row,
    [Button.name]: Button,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },

  props: {
    modelValue: {
      default: () => ({}),
      type: Object,
    },
  },

  setup(props) {
    const urlFilterOptions = ref([])
    const activeTab = ref(0)
    const parameters = ref([])
    const apps = ref([])
    const urls = ref([])
    const selectedApp = ref({})
    const selectedFilter = ref(undefined)
    const protocol = ref('https://')
    const loadingApps = ref(true)
    const data = ref({ path: '' })
    const hasCustomSlug = ref(false)
    const customUrl = ref(false)
    const generatedUrl = ref('')
    const modal = inject('akModal')
    const historyPerPage = ref(8)
    const historyTotal = ref(0)
    const historyLoading = ref(false)

    const queryString = computed(() => {
      return createQueryString(
        parameters.value.reduce((acc, curr) => {
          acc[curr.key] = curr.value
          return acc
        }, {}),
      )
    })

    const constructedUrl = computed(() => {
      return `${protocol.value}${data.value.path}${
        queryString.value && queryString.value.length > 1
          ? queryString.value
          : ''
      }`
    })

    const updateParam = (param, index) => {
      merge(parameters.value[index], param)
    }

    const addParam = () => {
      parameters.value.push({
        type: '',
        key: '',
        value: '',
      })
    }

    const removeParam = (index) => {
      parameters.value.splice(index, 1)
    }

    const fetchUrls = async (page = 1) => {
      historyLoading.value = true
      const filter = {}

      if (selectedFilter.value) {
        if (selectedFilter.value.isCustom) {
          filter.isCustom = true
        } else if (!selectedFilter.value.showAll) {
          filter['app.id'] = selectedFilter.value._id
        }
      }

      const { data: res } = await request.get('/urls', {
        params: {
          agency: agencyObj()._id,
          account: accountObj()._id,
          type: JSON.stringify({ $ne: 'tracking' }),
          deleted: false,
          per_page: historyPerPage.value,
          page,
          ...filter,
        },
      })

      urls.value = res.data
      historyTotal.value = res.meta.pagination.total
      historyLoading.value = false
    }

    const fetchApps = async () => {
      const { data: res } = await request.get('/apps', {
        params: {
          agency: agencyObj()._id,
          account: accountObj()._id,
          per_page: 0,
          publishedStatus: true,
          type: JSON.stringify({ $nin: ['embed'] }),
          filters: '-live,-draft',
        },
      })

      const appData = res.data
      const filterData = [...appData]

      appData.unshift(customOption)
      filterData.unshift(customOption)
      filterData.unshift(allOption)

      apps.value = appData
      urlFilterOptions.value = filterData

      return res.data
    }

    const selectApp = (app) => {
      protocol.value = 'https://'
      selectedApp.value = app
      customUrl.value = app.isCustom ? true : false
      if (app.customSlug) {
        hasCustomSlug.value = true
      } else {
        hasCustomSlug.value = false
      }
      makeUrl(app)
      selectFilter(app)
    }

    const makeUrl = (app) => {
      if (app.isCustom) {
        data.value.path = ''
      } else {
        const slug = app.customSlug ? app.customSlug : app.slug
        const acc = app.customSlug ? '/' : `/${accountObj().slug}/`
        const pubUrl = makePublishDomain(app)
        data.value.path = pubUrl + acc + slug
      }
    }

    const generateShortURL = async () => {
      try {
        const shortUrlOptions = { url: constructedUrl.value }

        if (!selectedApp.value.isCustom) {
          shortUrlOptions.app = {
            title: selectedApp.value.title,
            id: selectedApp.value._id,
          }
        } else {
          shortUrlOptions.isCustom = true
        }

        const { data: res } = await request.post(
          '/urls/shorten',
          shortUrlOptions,
        )

        generatedUrl.value = res.data.url

        sendEvent({
          eventName: 'Created Short URL',
          metaData: {
            short: res.data.url,
            full: constructedUrl.value,
          },
        })

        ToastProgrammatic.open({
          message: 'URL has been created.',
          type: 'success',
        })
      } catch (ex) {
        ToastProgrammatic.open({
          message: 'Failed to create URL.',
          type: 'danger',
        })
      }
    }

    const selectFilter = async (filter) => {
      selectedFilter.value = filter
      await fetchUrls()
    }

    const tabChanged = async (tab) => {
      if (tab === 1) {
        await fetchUrls()
      }
    }

    const changeProtocol = () => {
      if (selectedApp.value.isCustom) {
        protocol.value = protocol.value === 'https://' ? 'http://' : 'https://'
      }
    }

    const close = (...args) => {
      modal.close(...args)
    }

    onMounted(async () => {
      const apps = await fetchApps()
      const selected = apps.find((i) => i._id === props.modelValue._id)
      if (selected) {
        selectApp(selected)
      }
      loadingApps.value = false
    })

    return {
      close,
      urlFilterOptions,
      activeTab,
      parameters,
      addParam,
      removeParam,
      updateParam,
      apps,
      urls,
      selectedApp,
      selectedFilter,
      protocol,
      loadingApps,
      selectApp,
      data,
      hasCustomSlug,
      customUrl,
      queryString,
      constructedUrl,
      fetchUrls,
      tabChanged,
      selectFilter,
      generateShortURL,
      changeProtocol,
      generatedUrl,
      historyPerPage,
      historyTotal,
      historyLoading,
    }
  },
})
</script>
